import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useAPI from "../../../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../../../api/hooks/useColors";
import useEntityIsCriticalAsset from "../../../../../../../../../api/hooks/useEntityIsCriticalAsset";
import { entityKeys } from "../../../../../../../../../api/queryKeys";
import LoadingIcon from "../../../../../../../../../components/LoadingIcon";
import {
  getEntityTypeFromEntityId,
  getSimpleEntityDisplayNameFromId,
  isComplexEntityType,
} from "../../../../../../../../../utils/utils";
import { getIncludedEntityIds } from "../../../../../EntitiesSection/utils";
import GroupedControlEntitiesSelection from "./GroupedControlEntitiesSelection";
import NonGroupedControlEntitiesTable from "./NonGroupedControlEntitiesTable";

const getEntitiesToFetch = (controlIncludedEntityIds) => {
  return controlIncludedEntityIds.filter((entityId) =>
    isComplexEntityType(getEntityTypeFromEntityId(entityId))
  );
};

const getEntityRow = (entityId, fetchedEntities, entityIsCriticalAsset) => {
  const entityType = getEntityTypeFromEntityId(entityId);
  let displayName = getSimpleEntityDisplayNameFromId(entityId);
  let isCriticalAsset = false;
  if (isComplexEntityType(entityType)) {
    const entity = fetchedEntities.find(
      (entity) => entity.entity_id === entityId
    );
    if (entity) {
      displayName = entity.display_name;
      isCriticalAsset = entityIsCriticalAsset(entityId);
    }
  }
  return {
    entityId: entityId,
    isCriticalAsset: isCriticalAsset,
    entityType: entityType,
    displayName: displayName,
  };
};

const getRows = (
  controlIncludedEntityIds,
  fetchedEntities,
  entityIsCriticalAsset
) => {
  if (!fetchedEntities) return [];
  return controlIncludedEntityIds.map((entityId) =>
    getEntityRow(entityId, fetchedEntities, entityIsCriticalAsset)
  );
};

export default function EntitiesStep({
  fullControl,
  selectedEntityIDsMapping,
  setSelectedEntityIDsMapping,
}) {
  const { fetchEntitiesBatch } = useAPI();
  const colors = useColors();

  const { entityIsCriticalAsset, assetsAreLoading } =
    useEntityIsCriticalAsset();

  const controlIncludedEntityIds = getIncludedEntityIds(fullControl);
  let entityIDsToFetch = getEntitiesToFetch(controlIncludedEntityIds);

  const entitiesQuery = useQuery({
    queryKey: entityKeys.complexEntitiesByControl(fullControl.control_id),
    queryFn: () =>
      fetchEntitiesBatch(
        entityIDsToFetch,
        `auto-remediation-${fullControl.control_id}`
      ),
    select: (response) => response?.data,
  });

  if (assetsAreLoading || entitiesQuery.isLoading)
    return (
      <Box display="flex" justifyContent="center">
        <LoadingIcon size={25} />
      </Box>
    );

  const rows = getRows(
    controlIncludedEntityIds,
    entitiesQuery.data,
    entityIsCriticalAsset
  );
  const rowByEntityId = rows.reduce((acc, row) => {
    acc[row.entityId] = row;
    return acc;
  }, {});

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="subtitle2" color={colors.text_primary}>
          Affected entities
        </Typography>
        <Typography variant="body2" color={colors.text_secondary}>
          Only the entities you select will be affected.
        </Typography>
      </Box>
      <Box>
        {fullControl.is_grouped ? (
          <GroupedControlEntitiesSelection
            fullControl={fullControl}
            rowByEntityId={rowByEntityId}
            selectedEntityIDsMapping={selectedEntityIDsMapping}
            setSelectedEntityIDsMapping={setSelectedEntityIDsMapping}
          />
        ) : (
          <NonGroupedControlEntitiesTable
            rows={rows}
            selectedEntityIDsMapping={selectedEntityIDsMapping}
            setSelectedEntityIDsMapping={setSelectedEntityIDsMapping}
          />
        )}
      </Box>
    </Box>
  );
}
